import React, { useState, useEffect, useMemo } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 1100, margin: "0 auto" },
  header: { marginBottom: theme.spacing(3) },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  filterLabel: { fontWeight: 600, marginRight: theme.spacing(0.5) },
  section: { marginBottom: theme.spacing(4) },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(1.5), fontSize: "1rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary },
  card: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
  },
  cardTitle: { fontWeight: 600, flex: 1 },
  cardDesc: { color: theme.palette.text.secondary, fontSize: "0.875rem", marginTop: theme.spacing(0.5), marginBottom: theme.spacing(1) },
  techBlock: { marginTop: theme.spacing(1) },
  techRow: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", marginBottom: theme.spacing(0.5), alignItems: "flex-start" },
  techLabel: { fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", color: theme.palette.text.secondary, minWidth: 100, paddingTop: 3 },
  techValue: { fontSize: "0.8rem", fontFamily: "monospace", color: theme.palette.text.primary },
  behaviorList: { margin: "4px 0", paddingLeft: 20, "& li": { fontSize: "0.82rem", marginBottom: 2 } },
  // todo card
  todoCard: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  todoCardBug: { borderLeftColor: theme.palette.error.main },
  todoCardBacklog: { borderLeftColor: theme.palette.text.disabled },
  todoCardBlocked: { borderLeftColor: "#9c27b0" },
  loading: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
  tabBar: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(3) },
  chip: { cursor: "pointer" },
  sinceChip: { fontSize: "0.7rem", height: 20 },
  targetChip: { fontSize: "0.7rem", height: 20, background: "#fff9c4" },
}));

const STATUS_COLORS = { stable: "primary", beta: "default", deprecated: "secondary" };
const STATUS_LABELS = { stable: "estable", beta: "beta", experimental: "experimental", deprecated: "obsoleto" };
const TYPE_COLORS = { bug: "secondary", feature: "primary", design: "default", research: "default", refactor: "default" };
const TYPE_LABELS = { feature: "feature", bug: "bug", design: "diseño", research: "investigación", refactor: "refactor" };
const PRIORITY_ORDER = ["next", "soon", "backlog", "blocked"];
const PRIORITY_LABELS = { next: "Próximo", soon: "Pronto", backlog: "Backlog", blocked: "Bloqueado" };

function arrayField(label, values) {
  if (!values || values.length === 0) return null;
  return { label, value: values.join(" · ") };
}

function FeatureCard({ feat }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const techFields = [
    arrayField("API endpoints", feat.api_endpoints),
    arrayField("Eventos socket", feat.socket_events),
    arrayField("Campos DB", feat.db_fields),
    arrayField("Campos configurables", feat.config_fields),
    arrayField("Elementos UI", feat.ui_elements),
    arrayField("Archivos clave", feat.key_files),
    arrayField("Depende de", feat.depends_on),
    arrayField("Clientes", feat.companies),
  ].filter(Boolean);

  const hasTech = techFields.length > 0 || (feat.behavior && feat.behavior.length > 0);

  return (
    <Paper className={classes.card} elevation={1}>
      <div className={classes.cardHeader} onClick={() => hasTech && setOpen(o => !o)}>
        <Typography className={classes.cardTitle}>{feat.name}</Typography>
        <Chip label={feat.id} size="small" variant="outlined" style={{ fontSize: "0.7rem", height: 20 }} />
        <Chip label={STATUS_LABELS[feat.status] || feat.status} size="small" color={STATUS_COLORS[feat.status] || "default"} className={classes.sinceChip} />
        {feat.since && <Chip label={feat.since} size="small" variant="outlined" className={classes.sinceChip} />}
        {hasTech && (
          <IconButton size="small" style={{ padding: 2 }}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </div>
      <Typography className={classes.cardDesc}>{feat.description}</Typography>
      {hasTech && (
        <Collapse in={open}>
          <Divider style={{ marginBottom: 8 }} />
          <div className={classes.techBlock}>
            {feat.behavior && feat.behavior.length > 0 && (
              <div className={classes.techRow}>
                <span className={classes.techLabel}>comportamiento</span>
                <ul className={classes.behaviorList}>
                  {feat.behavior.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
            {techFields.map(({ label, value }) => (
              <div key={label} className={classes.techRow}>
                <span className={classes.techLabel}>{label}</span>
                <span className={classes.techValue}>{value}</span>
              </div>
            ))}
          </div>
        </Collapse>
      )}
    </Paper>
  );
}

function TodoCard({ todo }) {
  const classes = useStyles();
  const cardClass = [
    classes.todoCard,
    todo.type === "bug" ? classes.todoCardBug : "",
    todo.priority === "backlog" ? classes.todoCardBacklog : "",
    todo.priority === "blocked" ? classes.todoCardBlocked : "",
  ].join(" ");

  return (
    <Paper className={cardClass} elevation={1}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Typography style={{ fontWeight: 600, flex: 1 }}>{todo.name}</Typography>
        <Chip label={todo.id} size="small" variant="outlined" style={{ fontSize: "0.7rem", height: 20 }} />
        <Chip label={TYPE_LABELS[todo.type] || todo.type} size="small" color={TYPE_COLORS[todo.type] || "default"} style={{ fontSize: "0.7rem", height: 20 }} />
        {todo.target_client && (
          <Chip label={`empresa ${todo.target_client}`} size="small" className={classes.targetChip} />
        )}
      </div>
      <Typography style={{ color: "#555", fontSize: "0.875rem" }}>{todo.description}</Typography>
      {todo.context && (
        <Typography style={{ color: "#999", fontSize: "0.78rem", marginTop: 4, fontStyle: "italic" }}>
          {todo.context}
        </Typography>
      )}
      {todo.depends_on && todo.depends_on.length > 0 && (
        <Typography style={{ fontSize: "0.72rem", marginTop: 4, color: "#777" }}>
          Depende de: {todo.depends_on.join(", ")}
        </Typography>
      )}
    </Paper>
  );
}

const ALL_MODULES = "Todos";

export default function SuperAdminDocs() {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("features");
  const [moduleFilter, setModuleFilter] = useState(ALL_MODULES);
  const [priorityFilter, setPriorityFilter] = useState(ALL_MODULES);

  useEffect(() => {
    api.get("/superadmin/docs")
      .then(({ data: d }) => setData(d))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, []);

  const featureModules = useMemo(() => {
    if (!data) return [];
    return [ALL_MODULES, ...Array.from(new Set(data.features.map(f => f.module))).sort()];
  }, [data]);

  const groupedFeatures = useMemo(() => {
    if (!data) return {};
    const filtered = moduleFilter === ALL_MODULES ? data.features : data.features.filter(f => f.module === moduleFilter);
    return filtered.reduce((acc, feat) => {
      (acc[feat.module] = acc[feat.module] || []).push(feat);
      return acc;
    }, {});
  }, [data, moduleFilter]);

  const groupedTodos = useMemo(() => {
    if (!data) return {};
    const filtered = priorityFilter === ALL_MODULES ? data.todos : data.todos.filter(t => t.priority === priorityFilter);
    return filtered.reduce((acc, todo) => {
      (acc[todo.priority] = acc[todo.priority] || []).push(todo);
      return acc;
    }, {});
  }, [data, priorityFilter]);

  if (loading) return <div className={classes.loading}><CircularProgress /></div>;
  if (!data) return null;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 700 }}>Feature & Pendientes Tracker</Typography>
        <Typography variant="body2" color="textSecondary">
          {data.features.length} features implementados · {data.todos.length} pendientes
        </Typography>
      </div>

      <div className={classes.tabBar}>
        <Chip
          label={`Features (${data.features.length})`}
          color={activeTab === "features" ? "primary" : "default"}
          onClick={() => setActiveTab("features")}
          className={classes.chip}
        />
        <Chip
          label={`Pendientes (${data.todos.length})`}
          color={activeTab === "todos" ? "primary" : "default"}
          onClick={() => setActiveTab("todos")}
          className={classes.chip}
        />
      </div>

      {activeTab === "features" && (
        <>
          <div className={classes.filters}>
            <Typography variant="body2" className={classes.filterLabel}>Módulo:</Typography>
            {featureModules.map(m => (
              <Chip
                key={m}
                label={m === ALL_MODULES ? "Todos los módulos" : m}
                size="small"
                color={moduleFilter === m ? "primary" : "default"}
                variant={moduleFilter === m ? "default" : "outlined"}
                onClick={() => setModuleFilter(m)}
                className={classes.chip}
              />
            ))}
          </div>
          {Object.keys(groupedFeatures).sort().map(module => (
            <div key={module} className={classes.section}>
              <Typography className={classes.sectionTitle}>{module} ({groupedFeatures[module].length})</Typography>
              {groupedFeatures[module].map(feat => <FeatureCard key={feat.id} feat={feat} />)}
            </div>
          ))}
        </>
      )}

      {activeTab === "todos" && (
        <>
          <div className={classes.filters}>
            <Typography variant="body2" className={classes.filterLabel}>Prioridad:</Typography>
            {[ALL_MODULES, ...PRIORITY_ORDER].map(p => (
              <Chip
                key={p}
                label={p === ALL_MODULES ? "Todas las prioridades" : PRIORITY_LABELS[p]}
                size="small"
                color={priorityFilter === p ? "primary" : "default"}
                variant={priorityFilter === p ? "default" : "outlined"}
                onClick={() => setPriorityFilter(p)}
                className={classes.chip}
              />
            ))}
          </div>
          {PRIORITY_ORDER.filter(p => groupedTodos[p]).map(priority => (
            <div key={priority} className={classes.section}>
              <Typography className={classes.sectionTitle}>
                {PRIORITY_LABELS[priority]} ({groupedTodos[priority].length})
              </Typography>
              {groupedTodos[priority].map(todo => <TodoCard key={todo.id} todo={todo} />)}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
